import React, {
  useMemo, useRef, useState, useEffect, useCallback, ReactElement, Fragment, RefObject,
} from 'react';
import ButtonBase from '../button-base/button-base.component';
import SwipeableSlider from '../swipeable-slider/swipeable-slider.component';
import FlexContainer from '../flex-container/flex-container.component';
import FormatService from '../../services/format-service';
import useResize from '../../hooks/use-resize';
import useStylesChevronSlider from './chevron-slider.styles';
import Chevron1 from '../../static/images/chevrons/chevron1.svg';
import Chevron2 from '../../static/images/chevrons/chevron2.svg';
import Chevron3 from '../../static/images/chevrons/chevron3.svg';
import Chevron4 from '../../static/images/chevrons/chevron4.svg';
import Chevron5 from '../../static/images/chevrons/chevron5.svg';
import Chevron6 from '../../static/images/chevrons/chevron6.svg';
import Chevron7 from '../../static/images/chevrons/chevron7.svg';
import Chevron8 from '../../static/images/chevrons/chevron8.svg';
import Chevron9 from '../../static/images/chevrons/chevron9.svg';
import Chevron10 from '../../static/images/chevrons/chevron10.svg';
import Chevron11 from '../../static/images/chevrons/chevron11.svg';
import Chevron12 from '../../static/images/chevrons/chevron12.svg';
import ChevronEnd from '../../static/images/chevrons/chevron-end.svg';
import './chevron-slider.styles.scss';

const ChevronSlider: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const CHEVRON_WIDTH: number = 175;
  const CHEVRON_HEIGHT: number = 60;

  const {
    activeValue,
    chevronItemList,
    controlsDeps,
    onChange,
  } = props;

  const { classes } = useStylesChevronSlider();
  const containerRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [withControlsVisible, setWithControlsVisible]: [boolean, Function] = useState(false);

  const defaultChevronItemList: ChevronItem[] = useMemo((): ChevronItem[] => [
    {
      id: 1,
      component: <Chevron1 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-1" />,
    },
    {
      id: 2,
      component: <Chevron2 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-2" />,
    },
    {
      id: 3,
      component: <Chevron3 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-3" />,
    },
    {
      id: 4,
      component: <Chevron4 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-4" />,
    },
    {
      id: 5,
      component: <Chevron5 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-5" />,
    },
    {
      id: 6,
      component: <Chevron6 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-6" />,
    },
    {
      id: 7,
      component: <Chevron7 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-7" />,
    },
    {
      id: 8,
      component: <Chevron8 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-7" />,
    },
    {
      id: 9,
      component: <Chevron9 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-7" />,
    },
    {
      id: 10,
      component: <Chevron10 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-7" />,
    },
    {
      id: 11,
      component: <Chevron11 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-7" />,
    },
    {
      id: 12,
      component: <Chevron12 width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} className="chevron-item-7" />,
    },
  ], []);

  const defaultChevronEndItem: ChevronItem = useMemo((): ChevronItem => ({
    id: 1,
    component: <ChevronEnd width={CHEVRON_WIDTH} height={CHEVRON_HEIGHT} />,
  }), []);

  const extendedChevronItemList: ChevronItem[] = useMemo((): ChevronItem[] => {
    const resultChevronItemList: ChevronItem[] = [];

    chevronItemList.forEach((chevronItem: ChevronItem, chevronItemIndex: number): void => {
      const defaultChevronItem: ChevronItem | null = defaultChevronItemList.find((
        defaultChevronItem: ChevronItem,
      ): boolean => (
        defaultChevronItem.id === chevronItem.id
      )) || null;

      if (defaultChevronItem && chevronItemIndex < chevronItemList.length - 1) {
        resultChevronItemList.push(defaultChevronItem ? {
          ...chevronItem,
          component: defaultChevronItem.component,
        } : chevronItem);
      }
    });

    if (chevronItemList.length > resultChevronItemList.length) {
      resultChevronItemList.push({
        ...chevronItemList[chevronItemList.length - 1],
        component: defaultChevronEndItem.component,
      });
    }

    return resultChevronItemList;
  }, [chevronItemList, defaultChevronItemList, defaultChevronEndItem]);

  const checkIfControlsVisible: Function = useCallback((): boolean => {
    const containerWidth: number | null = containerRef && containerRef.current && containerRef.current.clientWidth;

    return !!containerWidth && ((chevronItemList.length * CHEVRON_WIDTH) > containerWidth);
  }, [containerRef, chevronItemList]);

  const onResize: Function = useCallback((): void => {
    setWithControlsVisible(checkIfControlsVisible());
  }, [containerRef, chevronItemList]);

  useEffect((): void => {
    setWithControlsVisible(checkIfControlsVisible());
  }, [...(controlsDeps || [])]);

  useResize(onResize);

  if (!chevronItemList || !chevronItemList.length) {
    return <Fragment />;
  }

  return (
    <FlexContainer ref={containerRef} className={classes.chevronSliderContainer}>
      <SwipeableSlider withDarkControls withControlsVisible={withControlsVisible} slideItemWidth={CHEVRON_WIDTH}>
        {
          extendedChevronItemList.map((chevronItem: ChevronItem): ReactElement => {
            const isCurrentItemActive: boolean = !!chevronItem.value && chevronItem.value === activeValue;
            const selectedClassName: string | undefined = isCurrentItemActive ? classes.chevronSelected : undefined;
            const buttonSelectedClassName: string | undefined = isCurrentItemActive
              ? classes.chevronButtonSelected : undefined;

            return (
              <ButtonBase
                key={chevronItem.id}
                className={FormatService.formatClassNames(classes.chevronButton, buttonSelectedClassName)}
                onClick={(): void => {
                  onChange(chevronItem.value);
                }}
              >
                <FlexContainer isColumn className={FormatService.formatClassNames(classes.chevron, selectedClassName)}>
                  <FlexContainer isColumn className={classes.chevronElement}>
                    {chevronItem.component}
                  </FlexContainer>
                  <FlexContainer isColumn className={classes.chevronTitle}>
                    <FlexContainer>
                      {chevronItem.title}
                    </FlexContainer>
                    <FlexContainer className={classes.chevronSubTitle}>
                      {chevronItem.subTitle}
                    </FlexContainer>
                  </FlexContainer>
                </FlexContainer>
              </ButtonBase>
            );
          })
        }
      </SwipeableSlider>
    </FlexContainer>
  );
});

export default ChevronSlider;
